import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, Listing } from '@/lib/supabase';
import { ListingCard } from '@/components/listings/ListingCard';
import { Filter, Search, Loader2, MapPin } from 'lucide-react';
import { MAJOR_CITIES } from '@/lib/locations';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros locales
  const typeParam = searchParams.get('type') || 'all';
  const cityParam = searchParams.get('city') || '';

  // Estado para autocompletado del buscador
  const [searchTerm, setSearchTerm] = useState(cityParam);
  const [suggestions, setSuggestions] = useState<typeof MAJOR_CITIES>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(cityParam);
    fetchListings();
  }, [searchParams]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function fetchListings() {
    setLoading(true);
    try {
      let query = supabase
        .from('listings')
        .select('*')
        .eq('is_active', true)
        .eq('status', 'approved') // SOLO MOSTRAR APROBADOS
        .order('created_at', { ascending: false });

      if (typeParam !== 'all') {
        query = query.eq('operation_type', typeParam);
      }

      if (cityParam) {
        query = query.or(`city.ilike.%${cityParam}%,title.ilike.%${cityParam}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (searchTerm) prev.set('city', searchTerm);
      else prev.delete('city');
      return prev;
    });
    setShowSuggestions(false);
  };

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (val.length > 1) {
      const filtered = MAJOR_CITIES.filter(c => 
        c.name.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectCity = (cityName: string) => {
    setSearchTerm(cityName);
    setSearchParams(prev => {
      prev.set('city', cityName);
      return prev;
    });
    setShowSuggestions(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header de filtros */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 items-center">
            
            {/* Buscador de Ubicación con Autocomplete */}
            <div className="relative flex-1 w-full" ref={searchWrapperRef}>
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
              <input 
                type="text" 
                placeholder="Buscar por ciudad o zona..." 
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={searchTerm}
                onChange={handleSearchTermChange}
                autoComplete="off"
              />
              
              {/* Sugerencias Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden z-50">
                  {suggestions.map((city) => (
                    <button
                      key={`${city.name}-${city.province}`}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center justify-between group transition-colors text-sm"
                      onClick={() => handleSelectCity(city.name)}
                    >
                      <span className="font-medium text-slate-700 group-hover:text-blue-700">{city.name}</span>
                      <span className="text-xs text-slate-400 group-hover:text-blue-400">{city.province}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selector de Tipo */}
            <div className="flex bg-slate-100 p-1 rounded-lg w-full md:w-auto">
              <button
                type="button"
                onClick={() => setSearchParams(prev => { prev.set('type', 'all'); return prev; })}
                className={`flex-1 md:flex-none px-4 py-1.5 rounded-md text-sm font-medium transition-all ${typeParam === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setSearchParams(prev => { prev.set('type', 'rent'); return prev; })}
                className={`flex-1 md:flex-none px-4 py-1.5 rounded-md text-sm font-medium transition-all ${typeParam === 'rent' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Alquiler
              </button>
              <button
                type="button"
                onClick={() => setSearchParams(prev => { prev.set('type', 'sale'); return prev; })}
                className={`flex-1 md:flex-none px-4 py-1.5 rounded-md text-sm font-medium transition-all ${typeParam === 'sale' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Venta
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Resultados */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900">
            {listings.length} {listings.length === 1 ? 'trastero encontrado' : 'trasteros encontrados'}
            {cityParam && <span className="text-slate-500 font-normal text-lg ml-2">en "{cityParam}"</span>}
          </h1>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin mb-4" />
            <p className="text-slate-500">Buscando los mejores espacios...</p>
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 border-dashed">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No encontramos resultados</h3>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Intenta ajustar tus filtros o busca en otra zona. ¡Nuevos trasteros se publican cada día!
            </p>
            <button 
              onClick={() => setSearchParams({ type: 'all' })}
              className="text-blue-600 font-medium hover:underline"
            >
              Ver todos los trasteros
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
